import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Grid,
  Box,
  Paper,
  InputBase,
  IconButton,
  Skeleton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ProductCard from '../ProductCard/ProductCard'
import EndOfPage from '../EndOfPage/EndOfPage'
import api from '../../config/api'
import {
  API_URL,
  isAuthenticated,
  API_WITH_OUT_PROTOCOL,
} from '../../config/config'

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

const ProductsGrid = ({
  searchUri: searchUri,
  payload_id: payload_id,
  category_id: category_id,
}) => {
  const [searchUrl, setSearchUri] = useState(
    searchUri || '/API_FRONT/store/get-list/',
  )
  const [products, setProducts] = useState([])
  const productsRef = useRef([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const observer = useRef()
  const socketRef = useRef(null)
  const isConnected = useRef(false)

  useEffect(() => {
    fetchProducts(true)
  }, [debouncedSearchQuery])

  useEffect(() => {
    const connectWebSocket = () => {
      const token = localStorage.getItem('access_token')
      socketRef.current = new WebSocket(
        `ws://${API_WITH_OUT_PROTOCOL}/ws/stock/?token=${token}`,
      )

      socketRef.current.onopen = () => {
        console.log('WebSocket подключен')
      }

      socketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('Полученные данные:', data)

          if (data.product_id && typeof data.quantity === 'number') {
            updateStock(data)
          } else {
            console.warn('Некорректные данные WebSocket:', data)
          }
        } catch (error) {
          console.error('Ошибка парсинга WebSocket данных:', error)
        }
      }

      socketRef.current.onerror = (error) => {
        console.error('WebSocket ошибка:', error)
      }

      socketRef.current.onclose = () => {
        console.log('WebSocket отключен, переподключение...')
        setTimeout(connectWebSocket, 3000)
      }
    }
    if (isAuthenticated()) {
      connectWebSocket()
    }

    return () => {
      socketRef.current?.close()
    }
  }, [])

  const updateStock = (data) => {
    console.log('Обновление товара:', data)
    setProducts((prevProducts) => {
      const updatedProducts = prevProducts.map((product) =>
        product.id === data.product_id
          ? { ...product, count: data.quantity }
          : product,
      )

      return [...updatedProducts] // Создаем новый массив для ререндера
    })
  }

  const fetchProducts = async (reset = false) => {
    if (loading || (!hasMore && !reset)) return

    setLoading(true)

    try {
      const params = {
        page: reset ? 1 : products.length / 20 + 1,
        payload_id: payload_id || 0,
        category_id: category_id || 0,
        ...(debouncedSearchQuery.trim() && {
          search: debouncedSearchQuery.trim(),
        }),
      }

      const response = await api.post(searchUrl, params)
      const newProducts = response.data.results

      setHasMore(response.data.total_pages > params.page)
      productsRef.current = reset
        ? newProducts
        : [...productsRef.current, ...newProducts]
      setProducts([...productsRef.current])
    } catch (error) {
      console.error('Ошибка загрузки товаров:', error)
    } finally {
      setLoading(false)
    }
  }

  const lastProductRef = useCallback(
    (node) => {
      if (loading || !hasMore) return

      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchProducts()
        }
      })

      if (node) observer.current.observe(node)
    },
    [loading, hasMore],
  )

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        component="form"
        sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
      >
        <InputBase
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Поиск товаров..."
          sx={{ flexGrow: 1 }}
        />
        <IconButton
          onClick={() => fetchProducts(true)}
          disabled={!searchQuery.trim()}
        >
          <SearchIcon />
        </IconButton>
        {searchQuery && (
          <IconButton onClick={() => setSearchQuery('')}>
            <ClearIcon />
          </IconButton>
        )}
      </Paper>
      <Grid container spacing={2}>
        {products.length === 0 && loading
          ? Array.from({ length: 20 }).map((_, index) => (
              <Grid
                size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                key={`skeleton-${index}`}
              >
                <Skeleton
                  variant="rectangular"
                  height={200}
                  sx={{ borderRadius: '8px', mb: 1 }}
                />
                <Skeleton variant="text" width="70%" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" />
              </Grid>
            ))
          : products.map((product, index) => (
              <Grid
                size={{ xs: 6, sm: 4, md: 3 }}
                key={product.id}
                ref={index === products.length - 1 ? lastProductRef : null}
              >
                <ProductCard product={product} />
              </Grid>
            ))}
      </Grid>
      {!loading && !hasMore && <EndOfPage />}
    </Box>
  )
}

export default ProductsGrid
